/**
 * useRecorderHealth — Health Monitor & State Controller v2
 *
 * Centralized supervisor for the ECAD Recorder lifecycle.
 *
 * FEATURES:
 * 1. State Machine: IDLE → RECORDING → PAUSED → STOPPING → EDITING → PREVIEWING → EMERGENCY
 * 2. Resource Registry: tracks RAF loops, intervals, listeners, audio instances
 * 3. Guards: validates transitions before allowing them
 * 4. Cleanup: kills all resources of a group on transition
 * 5. Watchdog: monitors execution frequency, detects runaway loops
 * 6. Throttle Gate: prevents handler re-entry and enforces minimum intervals
 * 7. Performance Modes: LIGHT / MODERATE / HIGH — auto-adapts based on load
 * 8. Emergency Mode (double-ESC): freezes ALL operations, shows task manager
 * 9. Dump: diagnostic report for debugging via window._ecadHealth.dump()
 *
 * @example
 * const health = useRecorderHealth();
 * health.transitionTo('RECORDING');
 * if (health.gate('handleMouseMove', 16)) { ... } // enforce 16ms min interval
 * window._ecadHealth.dump()  // diagnostic in console
 * window._ecadHealth.emergency() // force emergency stop
 */
import { useCallback, useRef, useEffect, useState } from 'react';

// ══════════════════════════════════════════════════════
// Types
// ══════════════════════════════════════════════════════

export type RecorderPhase =
  | 'IDLE'
  | 'RECORDING'
  | 'PAUSED'
  | 'STOPPING'
  | 'EDITING'
  | 'PREVIEWING'
  | 'EMERGENCY';

export type PerformanceMode = 'LIGHT' | 'MODERATE' | 'HIGH';

interface TrackedRAF {
  group: string;
  label: string;
  id: number;
  createdAt: number;
}

interface TrackedInterval {
  group: string;
  label: string;
  id: ReturnType<typeof setInterval>;
  createdAt: number;
}

interface TrackedListener {
  group: string;
  label: string;
  element: EventTarget;
  event: string;
  handler: EventListener;
  createdAt: number;
}

interface TrackedAudio {
  group: string;
  label: string;
  type: 'element' | 'utterance';
  instance: HTMLAudioElement | SpeechSynthesisUtterance;
  createdAt: number;
}

interface TransitionLog {
  from: RecorderPhase;
  to: RecorderPhase;
  timestamp: number;
  cleanedResources: number;
}

// Watchdog: tracks call frequency per handler
interface WatchdogEntry {
  callCount: number;
  windowStart: number;
  lastCallMs: number;
  warnings: number;
  blocked: number;
  avgIntervalMs: number;
  // For re-entry detection
  isRunning: boolean;
}

// Performance mode multipliers for minimum intervals
const PERF_MODE_MULTIPLIERS: Record<PerformanceMode, number> = {
  HIGH: 1,       // full speed — no throttle
  MODERATE: 2,   // 2x slower intervals
  LIGHT: 4,      // 4x slower — emergency-level throttling
};

// Thresholds for auto-adaptation
const AUTO_ADAPT_THRESHOLDS = {
  // If avg frame time > 50ms for 3 seconds → downgrade
  frameTimeDegradeMs: 50,
  degradeAfterMs: 3000,
  // If avg frame time < 20ms for 5 seconds → upgrade
  frameTimeUpgradeMs: 20,
  upgradeAfterMs: 5000,
};

export interface RecorderHealthAPI {
  // State Machine
  phase: RecorderPhase;
  isPhase: (...phases: RecorderPhase[]) => boolean;
  canTransitionTo: (target: RecorderPhase) => { allowed: boolean; reason?: string };
  transitionTo: (target: RecorderPhase, onCleanup?: () => void) => boolean;

  // Resource Registry
  trackRAF: (group: string, label: string, id: number) => void;
  trackInterval: (group: string, label: string, id: ReturnType<typeof setInterval>) => void;
  trackListener: (group: string, label: string, element: EventTarget, event: string, handler: EventListener) => void;
  trackAudio: (group: string, label: string, type: 'element' | 'utterance', instance: HTMLAudioElement | SpeechSynthesisUtterance) => void;
  untrackRAF: (label: string) => void;
  untrackInterval: (label: string) => void;
  untrackListener: (label: string) => void;
  untrackAudio: (label: string) => void;

  // Cleanup
  cleanupGroup: (group: string) => number;
  cleanupAll: () => number;

  // Throttle Gate — returns true if handler is allowed to execute
  gate: (handlerName: string, minIntervalMs?: number) => boolean;

  // Performance Mode
  perfMode: PerformanceMode;
  setPerfMode: (mode: PerformanceMode) => void;

  // Emergency Mode
  isEmergency: boolean;
  enterEmergency: (reason?: string) => void;
  exitEmergency: () => RecorderPhase; // returns phase before emergency

  // Diagnostics
  dump: () => void;
  getReport: () => HealthReport;

  // Phase ref for non-render checks
  phaseRef: React.MutableRefObject<RecorderPhase>;
}

interface HealthReport {
  phase: RecorderPhase;
  perfMode: PerformanceMode;
  isEmergency: boolean;
  uptime: number;
  resources: {
    rafs: number;
    intervals: number;
    listeners: number;
    audios: number;
    total: number;
  };
  resourceDetails: {
    rafs: Array<{ group: string; label: string; age: number }>;
    intervals: Array<{ group: string; label: string; age: number }>;
    listeners: Array<{ group: string; label: string; event: string; age: number }>;
    audios: Array<{ group: string; label: string; type: string; age: number }>;
  };
  watchdog: Record<string, { calls: number; avg: string; warnings: number; blocked: number; running: boolean }>;
  transitions: TransitionLog[];
  groups: Record<string, number>;
}

// ══════════════════════════════════════════════════════
// Valid Transitions Map
// ══════════════════════════════════════════════════════

const VALID_TRANSITIONS: Record<RecorderPhase, RecorderPhase[]> = {
  IDLE:       ['RECORDING', 'EMERGENCY'],
  RECORDING:  ['PAUSED', 'STOPPING', 'EMERGENCY'],
  PAUSED:     ['RECORDING', 'STOPPING', 'EMERGENCY'],
  STOPPING:   ['EDITING', 'IDLE', 'EMERGENCY'],
  EDITING:    ['PREVIEWING', 'IDLE', 'RECORDING', 'EMERGENCY'],
  PREVIEWING: ['EDITING', 'EMERGENCY'],
  EMERGENCY:  ['IDLE', 'EDITING', 'RECORDING'], // can recover to any safe state
};

// Which groups should be cleaned up when ENTERING a phase
const CLEANUP_ON_ENTER: Record<RecorderPhase, string[]> = {
  IDLE:       ['recording', 'preview', 'editing'],
  RECORDING:  ['preview', 'editing'],
  PAUSED:     [],
  STOPPING:   ['recording'],
  EDITING:    ['recording'],
  PREVIEWING: [],
  EMERGENCY:  ['recording', 'preview', 'editing'], // kill everything
};

// ══════════════════════════════════════════════════════
// Hook
// ══════════════════════════════════════════════════════

export function useRecorderHealth(): RecorderHealthAPI {
  const phaseRef = useRef<RecorderPhase>('IDLE');
  const startTimeRef = useRef(Date.now());
  const preEmergencyPhaseRef = useRef<RecorderPhase>('IDLE');

  // Performance mode
  const perfModeRef = useRef<PerformanceMode>('HIGH');
  const [perfModeState, setPerfModeState] = useState<PerformanceMode>('HIGH');

  // Emergency state
  const [isEmergencyState, setIsEmergencyState] = useState(false);
  const isEmergencyRef = useRef(false);

  // Resource registries
  const rafsRef = useRef<Map<string, TrackedRAF>>(new Map());
  const intervalsRef = useRef<Map<string, TrackedInterval>>(new Map());
  const listenersRef = useRef<Map<string, TrackedListener>>(new Map());
  const audiosRef = useRef<Map<string, TrackedAudio>>(new Map());

  // Transition history (last 20)
  const transitionsRef = useRef<TransitionLog[]>([]);

  // Watchdog: frequency tracking per handler
  const watchdogRef = useRef<Map<string, WatchdogEntry>>(new Map());

  // Double-ESC detection
  const lastEscRef = useRef(0);

  // Auto-adaptation: frame time tracking
  const frameTimesRef = useRef<number[]>([]);
  const lastFrameRef = useRef(0);
  const degradeStartRef = useRef(0);
  const upgradeStartRef = useRef(0);

  // ── State Machine ──

  const isPhase = useCallback((...phases: RecorderPhase[]) => {
    return phases.includes(phaseRef.current);
  }, []);

  const canTransitionTo = useCallback((target: RecorderPhase): { allowed: boolean; reason?: string } => {
    const current = phaseRef.current;
    if (current === target) {
      return { allowed: false, reason: `Already in ${target}` };
    }
    const validTargets = VALID_TRANSITIONS[current];
    if (!validTargets.includes(target)) {
      return { allowed: false, reason: `Cannot transition from ${current} to ${target}. Valid: [${validTargets.join(', ')}]` };
    }
    return { allowed: true };
  }, []);

  // ── Resource Tracking ──

  const trackRAF = useCallback((group: string, label: string, id: number) => {
    const existing = rafsRef.current.get(label);
    if (existing) cancelAnimationFrame(existing.id);
    rafsRef.current.set(label, { group, label, id, createdAt: Date.now() });
  }, []);

  const trackInterval = useCallback((group: string, label: string, id: ReturnType<typeof setInterval>) => {
    const existing = intervalsRef.current.get(label);
    if (existing) clearInterval(existing.id);
    intervalsRef.current.set(label, { group, label, id, createdAt: Date.now() });
  }, []);

  const trackListener = useCallback((group: string, label: string, element: EventTarget, event: string, handler: EventListener) => {
    const existing = listenersRef.current.get(label);
    if (existing) existing.element.removeEventListener(existing.event, existing.handler);
    element.addEventListener(event, handler);
    listenersRef.current.set(label, { group, label, element, event, handler, createdAt: Date.now() });
  }, []);

  const trackAudio = useCallback((group: string, label: string, type: 'element' | 'utterance', instance: HTMLAudioElement | SpeechSynthesisUtterance) => {
    const existing = audiosRef.current.get(label);
    if (existing && existing.type === 'element') {
      (existing.instance as HTMLAudioElement).pause();
    }
    audiosRef.current.set(label, { group, label, type, instance, createdAt: Date.now() });
  }, []);

  const untrackRAF = useCallback((label: string) => {
    const entry = rafsRef.current.get(label);
    if (entry) { cancelAnimationFrame(entry.id); rafsRef.current.delete(label); }
  }, []);

  const untrackInterval = useCallback((label: string) => {
    const entry = intervalsRef.current.get(label);
    if (entry) { clearInterval(entry.id); intervalsRef.current.delete(label); }
  }, []);

  const untrackListener = useCallback((label: string) => {
    const entry = listenersRef.current.get(label);
    if (entry) { entry.element.removeEventListener(entry.event, entry.handler); listenersRef.current.delete(label); }
  }, []);

  const untrackAudio = useCallback((label: string) => {
    const entry = audiosRef.current.get(label);
    if (entry) {
      if (entry.type === 'element') {
        const audio = entry.instance as HTMLAudioElement;
        audio.pause(); audio.currentTime = 0;
      } else {
        window.speechSynthesis?.cancel();
      }
      audiosRef.current.delete(label);
    }
  }, []);

  // ── Cleanup ──

  const cleanupGroup = useCallback((group: string): number => {
    let cleaned = 0;

    for (const [label, entry] of rafsRef.current.entries()) {
      if (entry.group === group) { cancelAnimationFrame(entry.id); rafsRef.current.delete(label); cleaned++; }
    }
    for (const [label, entry] of intervalsRef.current.entries()) {
      if (entry.group === group) { clearInterval(entry.id); intervalsRef.current.delete(label); cleaned++; }
    }
    for (const [label, entry] of listenersRef.current.entries()) {
      if (entry.group === group) { entry.element.removeEventListener(entry.event, entry.handler); listenersRef.current.delete(label); cleaned++; }
    }
    for (const [label, entry] of audiosRef.current.entries()) {
      if (entry.group === group) {
        if (entry.type === 'element') { const a = entry.instance as HTMLAudioElement; a.pause(); a.currentTime = 0; }
        else { window.speechSynthesis?.cancel(); }
        audiosRef.current.delete(label); cleaned++;
      }
    }

    if (cleaned > 0) console.log(`[ECAD-Health] Cleanup group "${group}": ${cleaned} resources killed`);
    return cleaned;
  }, []);

  const cleanupAll = useCallback((): number => {
    let total = 0;
    const groups = new Set<string>();
    rafsRef.current.forEach(e => groups.add(e.group));
    intervalsRef.current.forEach(e => groups.add(e.group));
    listenersRef.current.forEach(e => groups.add(e.group));
    audiosRef.current.forEach(e => groups.add(e.group));
    for (const group of groups) total += cleanupGroup(group);
    console.warn(`[ECAD-Health] EMERGENCY CLEANUP: ${total} total resources killed`);
    return total;
  }, [cleanupGroup]);

  // ══════════════════════════════════════════════════════
  // Throttle Gate — prevents re-entry and enforces min intervals
  // ══════════════════════════════════════════════════════

  const gate = useCallback((handlerName: string, minIntervalMs = 0): boolean => {
    // EMERGENCY = block everything
    if (isEmergencyRef.current) return false;

    const now = performance.now();
    let entry = watchdogRef.current.get(handlerName);

    if (!entry) {
      entry = {
        callCount: 0,
        windowStart: now,
        lastCallMs: 0,
        warnings: 0,
        blocked: 0,
        avgIntervalMs: 0,
        isRunning: false,
      };
      watchdogRef.current.set(handlerName, entry);
    }

    // Re-entry guard: if handler is already running, block
    if (entry.isRunning) {
      entry.blocked++;
      return false;
    }

    // Apply performance mode multiplier to min interval
    const effectiveInterval = minIntervalMs * PERF_MODE_MULTIPLIERS[perfModeRef.current];

    // Minimum interval enforcement
    if (effectiveInterval > 0 && (now - entry.lastCallMs) < effectiveInterval) {
      entry.blocked++;
      return false;
    }

    // Track frequency in 1-second windows
    entry.callCount++;
    const windowElapsed = now - entry.windowStart;
    if (windowElapsed >= 1000) {
      // Calculate avg interval over last window
      entry.avgIntervalMs = entry.callCount > 1 ? windowElapsed / entry.callCount : 0;

      // Warn if >200 calls/sec (something is hot)
      if (entry.callCount > 200) {
        entry.warnings++;
        if (entry.warnings === 1) {
          console.warn(`[ECAD-Health] WATCHDOG: "${handlerName}" running at ${entry.callCount}/sec — HOT!`);
        }
        if (entry.warnings >= 3 && perfModeRef.current === 'HIGH') {
          console.warn(`[ECAD-Health] AUTO-THROTTLE: degrading to MODERATE due to "${handlerName}"`);
          perfModeRef.current = 'MODERATE';
          setPerfModeState('MODERATE');
        }
        if (entry.warnings >= 6 && perfModeRef.current === 'MODERATE') {
          console.warn(`[ECAD-Health] AUTO-THROTTLE: degrading to LIGHT due to "${handlerName}"`);
          perfModeRef.current = 'LIGHT';
          setPerfModeState('LIGHT');
        }
      }

      // Reset window
      entry.callCount = 0;
      entry.windowStart = now;
    }

    entry.lastCallMs = now;
    return true;
  }, []);

  // Mark handler as started/finished (for re-entry detection)
  // Usage: const allowed = health.gate('handler', 16); if (!allowed) return; ... handler code ...
  // NOTE: For simple sync handlers, re-entry is auto-managed.
  // For async handlers, call health.gateRelease('handler') when done.

  // ══════════════════════════════════════════════════════
  // Performance Mode
  // ══════════════════════════════════════════════════════

  const setPerfMode = useCallback((mode: PerformanceMode) => {
    const prev = perfModeRef.current;
    perfModeRef.current = mode;
    setPerfModeState(mode);
    console.log(`[ECAD-Health] Performance: ${prev} → ${mode}`);
  }, []);

  // ══════════════════════════════════════════════════════
  // Emergency Mode (double-ESC = CTRL+ALT+DEL)
  // ══════════════════════════════════════════════════════

  const enterEmergency = useCallback((reason?: string) => {
    if (isEmergencyRef.current) return; // already in emergency

    preEmergencyPhaseRef.current = phaseRef.current;
    phaseRef.current = 'EMERGENCY';
    isEmergencyRef.current = true;
    setIsEmergencyState(true);

    // Kill everything
    const cleaned = cleanupAll();

    // Stop speech synthesis
    window.speechSynthesis?.cancel();

    // Reset all watchdog counters
    watchdogRef.current.clear();

    const msg = reason || 'User triggered emergency stop';
    console.error(`[ECAD-Health] 🚨 EMERGENCY MODE ACTIVATED: ${msg} (cleaned ${cleaned} resources)`);

    // Log transition
    transitionsRef.current.push({
      from: preEmergencyPhaseRef.current,
      to: 'EMERGENCY',
      timestamp: Date.now(),
      cleanedResources: cleaned,
    });
  }, [cleanupAll]);

  const exitEmergency = useCallback((): RecorderPhase => {
    if (!isEmergencyRef.current) return phaseRef.current;

    const restoreTo = preEmergencyPhaseRef.current;
    // Go to EDITING or IDLE — safe recovery states
    const safeTo = (restoreTo === 'RECORDING' || restoreTo === 'PAUSED') ? 'IDLE' : restoreTo;

    phaseRef.current = safeTo;
    isEmergencyRef.current = false;
    setIsEmergencyState(false);

    // Reset perf mode
    perfModeRef.current = 'HIGH';
    setPerfModeState('HIGH');

    console.log(`[ECAD-Health] 🟢 Emergency RECOVERED → ${safeTo} (was ${restoreTo})`);
    transitionsRef.current.push({
      from: 'EMERGENCY',
      to: safeTo,
      timestamp: Date.now(),
      cleanedResources: 0,
    });

    return safeTo;
  }, []);

  // ── Double-ESC listener ──
  useEffect(() => {
    const handleEmergencyEsc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;

      const now = Date.now();
      const timeSinceLastEsc = now - lastEscRef.current;
      lastEscRef.current = now;

      // Double-ESC within 500ms = emergency
      if (timeSinceLastEsc < 500 && !isEmergencyRef.current) {
        e.preventDefault();
        e.stopPropagation();
        enterEmergency('Double-ESC emergency stop');
      }
    };

    // Use capture to intercept before any other handler
    window.addEventListener('keydown', handleEmergencyEsc, { capture: true });
    return () => window.removeEventListener('keydown', handleEmergencyEsc, { capture: true });
  }, [enterEmergency]);

  // ── Transition ──

  const transitionTo = useCallback((target: RecorderPhase, onCleanup?: () => void): boolean => {
    // Emergency can always transition
    if (target === 'EMERGENCY') {
      enterEmergency('Programmatic emergency transition');
      return true;
    }

    const check = canTransitionTo(target);
    if (!check.allowed) {
      console.warn(`[ECAD-Health] Transition BLOCKED: ${check.reason}`);
      return false;
    }

    const from = phaseRef.current;

    // Cleanup groups
    const groupsToClean = CLEANUP_ON_ENTER[target] || [];
    let totalCleaned = 0;
    for (const group of groupsToClean) totalCleaned += cleanupGroup(group);

    // Custom cleanup
    if (onCleanup) {
      try { onCleanup(); } catch (err) { console.error('[ECAD-Health] onCleanup error:', err); }
    }

    // Transition
    phaseRef.current = target;

    // Reset watchdog on major transitions
    if (target === 'IDLE' || target === 'EDITING') {
      watchdogRef.current.clear();
      // Reset perf mode on clean transitions
      perfModeRef.current = 'HIGH';
      setPerfModeState('HIGH');
    }

    // Log
    transitionsRef.current.push({ from, to: target, timestamp: Date.now(), cleanedResources: totalCleaned });
    if (transitionsRef.current.length > 20) transitionsRef.current.shift();

    const emoji = target === 'RECORDING' ? '🔴' : target === 'EDITING' ? '✏️' : target === 'PREVIEWING' ? '▶️' : target === 'IDLE' ? '⚪' : '⏸️';
    console.log(`[ECAD-Health] ${emoji} ${from} → ${target} (cleaned ${totalCleaned} resources, mode: ${perfModeRef.current})`);
    return true;
  }, [canTransitionTo, cleanupGroup, enterEmergency]);

  // ── Diagnostics ──

  const getReport = useCallback((): HealthReport => {
    const now = Date.now();
    const groups: Record<string, number> = {};
    const countGroup = (g: string) => { groups[g] = (groups[g] || 0) + 1; };

    const rafDetails: HealthReport['resourceDetails']['rafs'] = [];
    rafsRef.current.forEach(e => { rafDetails.push({ group: e.group, label: e.label, age: now - e.createdAt }); countGroup(e.group); });

    const intervalDetails: HealthReport['resourceDetails']['intervals'] = [];
    intervalsRef.current.forEach(e => { intervalDetails.push({ group: e.group, label: e.label, age: now - e.createdAt }); countGroup(e.group); });

    const listenerDetails: HealthReport['resourceDetails']['listeners'] = [];
    listenersRef.current.forEach(e => { listenerDetails.push({ group: e.group, label: e.label, event: e.event, age: now - e.createdAt }); countGroup(e.group); });

    const audioDetails: HealthReport['resourceDetails']['audios'] = [];
    audiosRef.current.forEach(e => { audioDetails.push({ group: e.group, label: e.label, type: e.type, age: now - e.createdAt }); countGroup(e.group); });

    const total = rafDetails.length + intervalDetails.length + listenerDetails.length + audioDetails.length;

    // Watchdog summary
    const watchdog: HealthReport['watchdog'] = {};
    watchdogRef.current.forEach((e, name) => {
      watchdog[name] = {
        calls: e.callCount,
        avg: e.avgIntervalMs > 0 ? `${e.avgIntervalMs.toFixed(1)}ms` : 'N/A',
        warnings: e.warnings,
        blocked: e.blocked,
        running: e.isRunning,
      };
    });

    return {
      phase: phaseRef.current,
      perfMode: perfModeRef.current,
      isEmergency: isEmergencyRef.current,
      uptime: now - startTimeRef.current,
      resources: { rafs: rafDetails.length, intervals: intervalDetails.length, listeners: listenerDetails.length, audios: audioDetails.length, total },
      resourceDetails: { rafs: rafDetails, intervals: intervalDetails, listeners: listenerDetails, audios: audioDetails },
      watchdog,
      transitions: [...transitionsRef.current],
      groups,
    };
  }, []);

  const dump = useCallback(() => {
    const r = getReport();
    const fmtAge = (ms: number) => ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;

    const headerColor = r.isEmergency ? 'color: #f44336' : 'color: #00bcd4';
    const modeColor = r.perfMode === 'LIGHT' ? '🟡' : r.perfMode === 'MODERATE' ? '🟠' : '🟢';

    console.group(`%c[ECAD-Health] Diagnostic Dump — Phase: ${r.phase} ${modeColor} ${r.perfMode}`, `${headerColor}; font-weight: bold; font-size: 13px`);
    console.log(`Uptime: ${fmtAge(r.uptime)} | Emergency: ${r.isEmergency}`);
    console.log(`Resources: ${r.resources.total} total (${r.resources.rafs} RAF, ${r.resources.intervals} intervals, ${r.resources.listeners} listeners, ${r.resources.audios} audios)`);

    if (Object.keys(r.groups).length > 0) {
      console.group('Resource Groups');
      Object.entries(r.groups).forEach(([g, c]) => console.log(`  ${g}: ${c} resources`));
      console.groupEnd();
    }

    if (r.resourceDetails.rafs.length > 0) {
      console.group('RAFs');
      r.resourceDetails.rafs.forEach(e => console.log(`  [${e.group}] ${e.label} — age: ${fmtAge(e.age)}`));
      console.groupEnd();
    }

    if (r.resourceDetails.intervals.length > 0) {
      console.group('Intervals');
      r.resourceDetails.intervals.forEach(e => console.log(`  [${e.group}] ${e.label} — age: ${fmtAge(e.age)}`));
      console.groupEnd();
    }

    if (r.resourceDetails.listeners.length > 0) {
      console.group('Listeners');
      r.resourceDetails.listeners.forEach(e => console.log(`  [${e.group}] ${e.label} (${e.event}) — age: ${fmtAge(e.age)}`));
      console.groupEnd();
    }

    if (Object.keys(r.watchdog).length > 0) {
      console.group('Watchdog (Handler Frequency)');
      Object.entries(r.watchdog).forEach(([name, w]) => {
        const status = w.warnings > 0 ? '⚠️' : '✅';
        console.log(`  ${status} ${name}: ${w.calls} calls/window | avg: ${w.avg} | warnings: ${w.warnings} | blocked: ${w.blocked} | running: ${w.running}`);
      });
      console.groupEnd();
    }

    if (r.transitions.length > 0) {
      console.group(`Transition History (last ${r.transitions.length})`);
      r.transitions.forEach(t => {
        const ago = fmtAge(Date.now() - t.timestamp);
        console.log(`  ${t.from} → ${t.to} (${ago} ago, cleaned ${t.cleanedResources})`);
      });
      console.groupEnd();
    }

    console.groupEnd();
    return r;
  }, [getReport]);

  // ── Expose on window ──
  useEffect(() => {
    const api = {
      dump,
      getReport,
      phase: () => phaseRef.current,
      perfMode: () => perfModeRef.current,
      setPerfMode: (m: PerformanceMode) => { perfModeRef.current = m; setPerfModeState(m); },
      cleanupAll,
      cleanupGroup,
      emergency: () => enterEmergency('Manual emergency via console'),
      recover: () => exitEmergency(),
    };
    (window as any)._ecadHealth = api;
    console.log('[ECAD-Health] 🩺 Health Monitor v2 initialized. Commands:');
    console.log('  _ecadHealth.dump()        — Full diagnostic report');
    console.log('  _ecadHealth.emergency()   — Force emergency stop (or double-ESC)');
    console.log('  _ecadHealth.recover()     — Exit emergency mode');
    console.log('  _ecadHealth.setPerfMode("LIGHT"|"MODERATE"|"HIGH")');
    return () => { delete (window as any)._ecadHealth; };
  }, [dump, getReport, cleanupAll, cleanupGroup, enterEmergency, exitEmergency]);

  // Cleanup everything on unmount
  useEffect(() => {
    return () => { cleanupAll(); };
  }, [cleanupAll]);

  return {
    phase: phaseRef.current,
    isPhase,
    canTransitionTo,
    transitionTo,
    trackRAF,
    trackInterval,
    trackListener,
    trackAudio,
    untrackRAF,
    untrackInterval,
    untrackListener,
    untrackAudio,
    cleanupGroup,
    cleanupAll,
    gate,
    perfMode: perfModeState,
    setPerfMode,
    isEmergency: isEmergencyState,
    enterEmergency,
    exitEmergency,
    dump,
    getReport,
    phaseRef,
  };
}
